import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import UnlockClientModal from './UnlockClientModal';
import {
  getClientSessionKey,
  isClientUnlocked,
  unlockClientWithPassphrase,
} from '../crypto/sessionKeyStore';
import { bytesToBase64 } from '../crypto/bytes';

function ProcessingStatus({ currentJob }) {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlockingProcessing, setUnlockingProcessing] = useState(false);
  const [processingUnlockMessage, setProcessingUnlockMessage] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockClientRecord, setUnlockClientRecord] = useState(null);
  const [unlockError, setUnlockError] = useState('');
  const [unlockingClient, setUnlockingClient] = useState(false);
  const [pendingProcessingUnlock, setPendingProcessingUnlock] = useState(false);

  const activeJobId = jobId || currentJob?.job_id;

  useEffect(() => {
    if (activeJobId) {
      loadJobStatus();
      // Poll for updates every 5 seconds while processing
      const interval = setInterval(() => {
        if (job?.status === 'processing' || job?.status === 'pending') {
          loadJobStatus();
        }
      }, 5000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [activeJobId, job?.status]);

  const loadJobStatus = async () => {
    try {
      const data = await apiClient.getJobStatus(activeJobId, true);
      setJob(data);
      setFiles(data.files || []);
      setError(null);
    } catch (err) {
      setError('Failed to load job status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sendProcessingUnlock = async () => {
    if (!job?.job_id || !job?.client_id) {
      return;
    }

    const dekBytes = getClientSessionKey(job.client_id);
    if (!dekBytes) {
      throw new Error('Client is locked.');
    }

    const dekB64 = bytesToBase64(dekBytes);
    await apiClient.unlockProcessing(job.job_id, job.client_id, dekB64, 600);
    setProcessingUnlockMessage('Processing unlock active for 10 minutes.');
    await loadJobStatus();
  };

  const ensureClientUnlockedForProcessing = async () => {
    if (!job?.client_id) {
      return false;
    }
    if (isClientUnlocked(job.client_id)) {
      return true;
    }

    const client = await apiClient.getClient(job.client_id);
    if (!client?.key_bundle) {
      throw new Error('Client key bundle missing. Re-enroll client encryption settings.');
    }
    setUnlockClientRecord(client);
    setUnlockError('');
    setShowUnlockModal(true);
    return false;
  };

  const handleUnlockProcessing = async () => {
    setProcessingUnlockMessage('');
    setUnlockingProcessing(true);

    try {
      const unlocked = await ensureClientUnlockedForProcessing();
      if (!unlocked) {
        setPendingProcessingUnlock(true);
        return;
      }

      await sendProcessingUnlock();
    } catch (err) {
      setError(err.message || 'Failed to unlock processing session.');
    } finally {
      setUnlockingProcessing(false);
    }
  };

  const handleRevokeProcessingUnlock = async () => {
    if (!job?.job_id) {
      return;
    }

    try {
      await apiClient.revokeProcessingUnlock(job.job_id);
      setProcessingUnlockMessage('Processing unlock revoked.');
      await loadJobStatus();
    } catch (err) {
      setError(err.message || 'Failed to revoke processing unlock.');
    }
  };

  const handleClientUnlock = async (passphrase) => {
    if (!unlockClientRecord) {
      return;
    }

    try {
      setUnlockingClient(true);
      setUnlockError('');
      await unlockClientWithPassphrase(unlockClientRecord, passphrase);
      setShowUnlockModal(false);
      setUnlockClientRecord(null);

      if (pendingProcessingUnlock) {
        setPendingProcessingUnlock(false);
        await sendProcessingUnlock();
      }
    } catch (err) {
      setUnlockError(err.message || 'Unlock failed.');
    } finally {
      setUnlockingClient(false);
      setUnlockingProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      processing: 'badge-warning',
      completed: 'badge-success',
      failed: 'badge-error',
      sorted: 'badge-success',
      flagged: 'badge-warning',
      unsorted: 'badge-pending',
      error: 'badge-error',
    };
    return badges[status] || 'badge-pending';
  };

  const getCategoryName = (num) => {
    const categories = {
      1: 'Financial Affidavit',
      2: 'Tax Returns',
      3: 'IRS Forms/Transcripts',
      4: 'Paystubs',
      5: 'Income',
      6: 'Loan Applications',
      7: 'Real Estate',
      8: 'Bank Accounts',
      9: 'Brokerage Accounts',
      10: 'Retirement Accounts',
      11: 'Virtual Currency',
      12: 'Life Insurance',
      13: 'Health Insurance',
      14: 'Corporate Tax Returns',
      15: 'Indebtedness',
      16: 'Agreements',
      17: 'Modification Agreements',
      18: 'Unequal Distribution',
      19: 'Court Orders',
    };
    return categories[num] || `Category ${num}`;
  };

  if (loading) {
    return <div className="card text-center">Loading job status...</div>;
  }

  if (!activeJobId) {
    return (
      <div className="card">
        <h2 className="card-title mb-4">Processing Status</h2>
        <p className="text-muted text-center">
          No active job. Upload files to start processing.
        </p>
        <div className="text-center mt-4">
          <Link to="/upload" className="btn btn-primary">
            Go to Upload
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="badge badge-error">{error}</div>
        <button className="btn btn-primary mt-4" onClick={loadJobStatus}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Processing Status</h2>
          <span className={`badge ${getStatusBadge(job.status)}`}>
            {job.status}
          </span>
        </div>

        <div className="grid grid-3 mb-4">
          <div>
            <div className="text-sm text-muted">Job ID</div>
            <div className="text-sm">{job.job_id}</div>
          </div>
          <div>
            <div className="text-sm text-muted">Client</div>
            <div>{job.client_id}</div>
          </div>
          <div>
            <div className="text-sm text-muted">Mode</div>
            <div>{job.mode}</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between mb-2">
            <span>Progress</span>
            <span>
              {job.progress?.processed || 0} / {job.progress?.total || 0} files
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${job.progress?.percentage || 0}%` }}
            />
          </div>
          <div className="text-sm text-muted text-right mt-1">
            {(job.progress?.percentage || 0).toFixed(1)}%
          </div>
        </div>

        <div className="mb-4">
          <div className="text-sm text-muted mb-2">
            Processing unlock session
          </div>
          {job.unlock?.active ? (
            <div className="badge badge-success">
              Active until {new Date(job.unlock.expires_at).toLocaleTimeString()}
            </div>
          ) : (
            <div className="badge badge-warning">
              Locked - worker cannot decrypt encrypted input until unlocked
            </div>
          )}
          {processingUnlockMessage && (
            <div className="text-sm mt-2">{processingUnlockMessage}</div>
          )}
          <div className="mt-3 flex gap-2">
            {!job.unlock?.active && job.status !== 'completed' && (
              <button
                className="btn btn-primary"
                onClick={handleUnlockProcessing}
                disabled={unlockingProcessing}
              >
                {unlockingProcessing ? 'Unlocking...' : 'Unlock Processing'}
              </button>
            )}
            {job.unlock?.active && job.status !== 'completed' && (
              <button
                className="btn btn-secondary"
                onClick={handleRevokeProcessingUnlock}
              >
                Revoke Unlock
              </button>
            )}
          </div>
        </div>

        {job.queue && (
          <div className="grid grid-3 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{job.queue.pending}</div>
              <div className="text-sm text-muted">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{job.queue.in_flight}</div>
              <div className="text-sm text-muted">Processing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{job.queue.delayed}</div>
              <div className="text-sm text-muted">Delayed</div>
            </div>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="card">
          <h3 className="card-title mb-4">File Results</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Original File</th>
                <th>Category</th>
                <th>Confidence</th>
                <th>Status</th>
                <th>Renamed</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.file_id}>
                  <td>
                    <div className="file-name">{file.filename}</div>
                  </td>
                  <td>
                    {file.category_number ? (
                      <span>
                        {file.category_number}. {getCategoryName(file.category_number)}
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {file.confidence ? (
                      <span
                        className={
                          file.confidence >= 0.85
                            ? 'text-success'
                            : file.confidence >= 0.6
                            ? 'text-warning'
                            : 'text-error'
                        }
                      >
                        {(file.confidence * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(file.status)}`}>
                      {file.status}
                    </span>
                  </td>
                  <td>
                    {file.renamed_filename ? (
                      <span className="text-sm">{file.renamed_filename}</span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div className="flex justify-between">
          <Link to="/" className="btn btn-secondary">
            Back to Clients
          </Link>
          {job.status === 'completed' && (
            <Link to={`/files/${job.client_id}`} className="btn btn-primary">
              Browse Organized Files
            </Link>
          )}
        </div>
      </div>
      <UnlockClientModal
        open={showUnlockModal}
        clientName={unlockClientRecord?.client_name || job?.client_id}
        loading={unlockingClient}
        error={unlockError}
        onCancel={() => {
          setShowUnlockModal(false);
          setUnlockClientRecord(null);
          setPendingProcessingUnlock(false);
          setUnlockingProcessing(false);
        }}
        onUnlock={handleClientUnlock}
      />
    </div>
  );
}

export default ProcessingStatus;
